import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import FinancialAIAssistant from '@/components/FinancialAIAssistant';
import FinancialChart from '@/components/FinancialChart';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

interface ClienteFinanciero {
  id: string;
  nombre: string;
  ingresos: number;
  gastos: number;
  resultado: number;
  margen: number;
  riesgo: 'bajo' | 'medio' | 'alto';
}

export default function PanelFinancieroMejorado() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [clientesData, setClientesData] = useState<ClienteFinanciero[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<string | null>(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [ratios, setRatios] = useState({
    liquidez: 0,
    solvencia: 0,
    rentabilidad: 0,
    endeudamiento: 0
  });

  const loadFinancialData = useCallback(async () => {
    try {
      setLoadingData(true);

      if (!user?.id) {
        setClientesData([]);
        setRatios({ liquidez: 0, solvencia: 0, rentabilidad: 0, endeudamiento: 0 });
        return;
      }

      const resp = await fetch('/api/financial/summary-for-advisor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asesorId: user.id }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'No se pudo cargar datos financieros');
      }

      const payload = await resp.json();
      const financialData = payload.data || [];

      if (!financialData || financialData.length === 0) {
        setClientesData([]);
        setRatios({ liquidez: 0, solvencia: 0, rentabilidad: 0, endeudamiento: 0 });
        return;
      }

      // Fetch client names separately (client-side, harmless)
      const uniqueClientIds = [...new Set(financialData.map((fd: Record<string, unknown>) => fd.cliente_id))];
      const { data: clientesInfo } = await supabase
        .from('clientes')
        .select('id, razon_social, nombre_comercial')
        .in('id', uniqueClientIds);

      const clientesMap = new Map();
      clientesInfo?.forEach((c: Record<string, unknown>) => {
        clientesMap.set(c.id, c.nombre_comercial || c.razon_social || 'Cliente sin nombre');
      });

      // Group by cliente and take latest
      const clientesDataMap = new Map();

      financialData.forEach((fd: Record<string, unknown>) => {
        if (!clientesDataMap.has(fd.cliente_id)) {
          const ingresos = typeof fd.ingresos === 'string' ? parseFloat(fd.ingresos) : (fd.ingresos as number) || 0;
          const gastos = typeof fd.gastos === 'string' ? parseFloat(fd.gastos) : (fd.gastos as number) || 0;
          const resultado = typeof fd.resultado === 'string' ? parseFloat(fd.resultado) : (fd.resultado as number) || 0;
          const margen = typeof fd.margen === 'string' ? parseFloat(fd.margen) : (fd.margen as number) || 0;

          clientesDataMap.set(fd.cliente_id, {
            id: fd.cliente_id,
            nombre: clientesMap.get(fd.cliente_id) || `Cliente ${(fd.cliente_id as string).substring(0, 8)}`,
            ingresos: Number(ingresos) || 0,
            gastos: Number(gastos) || 0,
            resultado: Number(resultado) || 0,
            margen: Number(margen) || 0,
            riesgo: fd.riesgo_ia || 'bajo',
          });
        }
      });

      const clientes = Array.from(clientesDataMap.values());
      setClientesData(clientes);

      const totalIngresos = clientes.reduce((sum: number, c: ClienteFinanciero) => sum + (c.ingresos || 0), 0);
      const totalGastos = clientes.reduce((sum: number, c: ClienteFinanciero) => sum + (c.gastos || 0), 0);
      const totalResultado = clientes.reduce((sum: number, c: ClienteFinanciero) => sum + (c.resultado || 0), 0);

      setRatios({
        liquidez: totalGastos > 0 ? (totalIngresos / totalGastos) * 100 : 0,
        solvencia: totalIngresos > 0 ? (totalResultado / totalIngresos) * 100 : 0,
        rentabilidad: totalIngresos > 0 ? (totalResultado / totalIngresos) * 100 : 0,
        endeudamiento: totalIngresos > 0 ? (totalGastos / totalIngresos) * 100 : 0,
      });
    } catch (error) {
      console.error('[Panel Financiero] Error loading financial data:', error);
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadFinancialData();
    }
  }, [user, loading, router, loadFinancialData]);

  const handleUploadFinancialData = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv,.xlsx,.xls';
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('clienteId', selectedCliente || '');

      try {
        const response = await fetch('/api/financial-data/upload', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Error uploading file');

        toast.success('Datos financieros cargados correctamente');
        loadFinancialData();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al cargar datos');
      }
    };
    fileInput.click();
  };

  if (loading || loadingData) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pb-12 space-y-12 pt-8">
          <Skeleton className="h-12 w-72 rounded-huge" />
          <Skeleton className="h-4 w-96 rounded-huge" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-28 rounded-huge" />
            <Skeleton className="h-28 rounded-huge" />
            <Skeleton className="h-28 rounded-huge" />
          </div>
          <Skeleton className="h-48 rounded-huge" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-64 rounded-huge" />
            <Skeleton className="h-64 rounded-huge" />
          </div>
        </div>
      </Layout>
    );
  }

  const totalIngresos = clientesData.reduce((sum, c) => sum + c.ingresos, 0);
  const totalGastos = clientesData.reduce((sum, c) => sum + c.gastos, 0);
  const totalResultado = clientesData.reduce((sum, c) => sum + c.resultado, 0);

  return (
    <>
      <Head>
        <title>Panel Financiero - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 pt-8">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="page-title">Panel Financiero</h1>
              <p className="page-subtitle">
                Análisis financiero y ratios de todos tus clientes
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleUploadFinancialData}
                className="ghost-btn"
              >
                <Icon name="upload_file" className="text-[18px]" />
                Cargar Datos
              </button>
              <button
                onClick={() => setShowAIAssistant(!showAIAssistant)}
                className="ghost-btn"
              >
                <Icon name="smart_toy" className="text-[18px]" />
                Asistente IA
              </button>
            </div>
          </div>

          {/* Main Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="trending_up" className="text-[20px] text-emerald-600" />
                <span className="stat-label !mt-0">Ingresos Totales</span>
              </div>
              <p className="stat-value text-emerald-700">
                €{totalIngresos.toLocaleString('es-ES')}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mt-2">Todos los clientes</p>
            </div>
            <div className="bg-gradient-to-br from-white to-red-50/20 border border-red-200/40 rounded-huge p-8">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="trending_down" className="text-[20px] text-red-500" />
                <span className="stat-label !mt-0">Gastos Totales</span>
              </div>
              <p className="stat-value text-red-600">
                €{totalGastos.toLocaleString('es-ES')}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mt-2">
                {totalIngresos > 0 ? ((totalGastos / totalIngresos) * 100).toFixed(1) : '0.0'}% de ingresos
              </p>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="account_balance" className="text-[20px] text-calm-text" />
                <span className="stat-label !mt-0">Resultado Neto</span>
              </div>
              <p className="stat-value">
                €{totalResultado.toLocaleString('es-ES')}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mt-2">
                Margen: {totalIngresos > 0 ? ((totalResultado / totalIngresos) * 100).toFixed(1) : '0.0'}%
              </p>
            </div>
          </div>

          {/* Ratios Financieros */}
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
            <h2 className="section-title mb-8">Ratios Financieros</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="stat-label !mt-0">Liquidez</span>
                  <Badge variant="outline" className="badge-success text-[9px] uppercase tracking-wider">Bueno</Badge>
                </div>
                <div className="stat-value">
                  {ratios.liquidez.toFixed(1)}%
                </div>
                <Progress
                  value={Math.min(ratios.liquidez, 100)}
                  className="mt-3 [&>div]:bg-emerald-500"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="stat-label !mt-0">Solvencia</span>
                  <Badge variant="outline" className="badge-info text-[9px] uppercase tracking-wider">Óptimo</Badge>
                </div>
                <div className="stat-value">
                  {ratios.solvencia.toFixed(1)}%
                </div>
                <Progress
                  value={Math.abs(ratios.solvencia)}
                  className="mt-3 [&>div]:bg-slate-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="stat-label !mt-0">Rentabilidad</span>
                  <Badge variant="outline" className="badge-info text-[9px] uppercase tracking-wider">Excelente</Badge>
                </div>
                <div className="stat-value">
                  {ratios.rentabilidad.toFixed(1)}%
                </div>
                <Progress
                  value={Math.abs(ratios.rentabilidad)}
                  className="mt-3 [&>div]:bg-slate-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="stat-label !mt-0">Endeudamiento</span>
                  <Badge variant="outline" className="badge-warning text-[9px] uppercase tracking-wider">Moderado</Badge>
                </div>
                <div className="stat-value">
                  {ratios.endeudamiento.toFixed(1)}%
                </div>
                <Progress
                  value={Math.min(ratios.endeudamiento, 100)}
                  className="mt-3 [&>div]:bg-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Client Financial Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <h2 className="section-title mb-6">Desglose por Cliente</h2>
              {clientesData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-steel-azure-50 flex items-center justify-center text-steel-azure-400 mb-4 mx-auto">
                    <Icon name="analytics" className="text-[40px]" />
                  </div>
                  <p className="text-sm font-light text-calm-muted mb-2">No hay datos financieros disponibles</p>
                  <p className="text-[11px] text-calm-accent font-light max-w-xs mx-auto">
                    Los datos financieros se generan automáticamente cuando se contabilizan documentos.
                    Asegúrate de que los documentos estén marcados como contabilizados.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {clientesData.map(cliente => (
                    <div
                      key={cliente.id}
                      className={cn(
                        'border rounded-2xl p-5 cursor-pointer transition-all duration-300',
                        selectedCliente === cliente.id
                          ? 'border-calm-text bg-slate-50'
                          : 'border-calm-border hover:border-calm-muted hover:bg-slate-50/50'
                      )}
                      onClick={() => setSelectedCliente(cliente.id)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-calm-text">{cliente.nombre}</h3>
                        <Badge
                          variant={cliente.riesgo === 'alto' ? 'destructive' : 'outline'}
                          className={cn(
                            'text-[9px] uppercase tracking-wider',
                            cliente.riesgo === 'medio' && 'badge-warning',
                            cliente.riesgo === 'bajo' && 'badge-success'
                          )}
                        >
                          {cliente.riesgo.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <span className="stat-label">Ingresos</span>
                          <p className="text-sm font-light text-emerald-600">
                            €{cliente.ingresos.toLocaleString('es-ES')}
                          </p>
                        </div>
                        <div>
                          <span className="stat-label">Gastos</span>
                          <p className="text-sm font-light text-red-500">
                            €{cliente.gastos.toLocaleString('es-ES')}
                          </p>
                        </div>
                        <div>
                          <span className="stat-label">Margen</span>
                          <p className={cn(
                            'text-sm font-light',
                            cliente.margen > 0 ? 'text-emerald-600' : 'text-red-500'
                          )}>
                            {cliente.margen.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chart */}
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <h2 className="section-title mb-6">Tendencia Financiera Agregada</h2>
              <FinancialChart 
                ingresos={totalIngresos}
                gastos={totalGastos}
                resultado={totalResultado}
              />
            </div>
          </div>
        </div>

        {/* AI Assistant Modal */}
        <Dialog open={showAIAssistant} onOpenChange={setShowAIAssistant}>
          <DialogContent className="max-w-4xl h-[600px] flex flex-col p-0">
            <DialogHeader className="p-6 pb-4 border-b border-calm-border">
              <DialogTitle className="section-title">Asistente Financiero IA</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <FinancialAIAssistant 
                clienteId={selectedCliente || undefined}
                asesorView={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    </>
  );
}
